"use client";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { STAGE_LABELS, STATUS_LABELS } from "@/components/file-status-badge";
import type { FileManagerTreeNode } from "@/hooks/use-file-manager-tree";
import { fileTypeLabel, formatBytes, formatDetailDate } from "./kb-utils";

const DetailRow = ({ label, value }: { label: string; value?: string | null }) => (
  <div>
    <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">{label}</p>
    <p className="mt-1 break-all text-sm text-foreground">{value ?? "—"}</p>
  </div>
);

export function KbNodeDetailsDialog({
  node,
  open,
  onOpenChange,
}: {
  node: FileManagerTreeNode | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="wrap-break-word pr-6 text-base leading-snug">
            {node?.name ?? "Details"}
          </DialogTitle>
        </DialogHeader>
        {node ? (
          <div className="grid gap-4">
            <DetailRow
              label="Kind"
              value={
                node.kind === "collection"
                  ? "Collection"
                  : node.kind === "team"
                    ? "Team"
                    : "File"
              }
            />
            {node.kind === "file" && (
              <>
                <DetailRow label="File type" value={fileTypeLabel(node.fileType)} />
                <DetailRow label="Size" value={node.size ? formatBytes(node.size) : undefined} />
                <DetailRow label="Uploaded by" value={node.uploadedBy} />
                <DetailRow
                  label="Status"
                  value={
                    node.processingStatus
                      ? (STATUS_LABELS[node.processingStatus.toUpperCase()] ?? node.processingStatus)
                      : undefined
                  }
                />
                {node.processingStage && node.processingStatus?.toUpperCase() !== "COMPLETED" && (
                  <DetailRow
                    label="Stage"
                    value={STAGE_LABELS[node.processingStage.toUpperCase()] ?? node.processingStage}
                  />
                )}
                {node.errorMessage && <DetailRow label="Error" value={node.errorMessage} />}
              </>
            )}
            {node.kind === "collection" && (
              <>
                <DetailRow label="Files" value={String(node.fileCount)} />
                <DetailRow label="Sharing" value={node.sharing} />
              </>
            )}
            <DetailRow label="Created" value={formatDetailDate(node.createdAt)} />
            <DetailRow label="Last updated" value={formatDetailDate(node.updatedAt)} />
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
